/**
 * Правила экрана «Посевной план» — фильтры, итоги и статус строки.
 *
 * Живут отдельно от компонента затем, что именно на них экран ловят на вранье:
 * «полей 214, назначено 190» в шапке обязано сойтись с тем, что видно в
 * таблице, а «только неназначенные» — показать РОВНО те поля, на которых
 * культуры нет. Проверять это рендером дорого и хрупко, функцией — дёшево.
 */
import type { VariantField } from '@/lib/planning/planCrops';

/** Как экран называет состояние поля. Это подпись, а не поле модели. */
export type FieldPlanStatus = 'assigned' | 'fallow' | 'unassigned' | 'by_subzones';

/**
 * Что стоит на поле в этом варианте.
 *
 * Пар (`occupied_by` без `plan_crop_id`) — НЕ «не назначено»: агроном принял
 * решение оставить поле отдыхать, и подгонять его в один список с забытыми
 * полями значило бы каждый год заново объяснять системе одно и то же.
 */
export function fieldPlanStatus(field: VariantField): FieldPlanStatus {
  if (field.occupied_by_subzones) return 'by_subzones';
  if (field.plan_crop_id) return 'assigned';
  if (field.occupied_by) return 'fallow';
  return 'unassigned';
}

/** Поля, на которых культуры в этом варианте нет. */
export function isUnassigned(field: VariantField): boolean {
  return fieldPlanStatus(field) === 'unassigned';
}

/** Поле правится прямо в строке — зоны правятся в карточке поля (ADR-049). */
export function isRowEditable(field: VariantField): boolean {
  return !field.occupied_by_subzones;
}

/**
 * Значение фильтра по культуре истории.
 *
 * `undefined` — «любой», `'unknown'` — «нет данных»: поле без истории это не
 * то же самое, что поле после пара, и сваливать их в одну кучу нельзя —
 * именно по полям без истории агроном решает вручную.
 */
export type HistoryFilter = string | undefined;

/** Фильтр плановой культуры: все / не назначена / конкретная культура. */
export type PlanFilter = string | undefined;

export const UNKNOWN_HISTORY = 'unknown';
export const PLAN_NONE = 'none';

export interface SowingPlanFilter {
  query: string;
  predecessor?: HistoryFilter;
  predecessor2?: HistoryFilter;
  plan?: PlanFilter;
}

function historyMatches(cropId: string | null, filter: HistoryFilter): boolean {
  if (!filter) return true;
  if (filter === UNKNOWN_HISTORY) return cropId === null;
  return cropId === filter;
}

function planMatches(field: VariantField, filter: PlanFilter): boolean {
  if (!filter) return true;
  if (filter === PLAN_NONE) return isUnassigned(field);
  return field.plan_crop_id === filter;
}

/** Варианты выпадашки истории — только то, что реально встречается в хозяйстве. */
export function historyOptions(
  fields: readonly VariantField[],
  which: 'predecessor' | 'predecessor2',
): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  let hasUnknown = false;
  for (const f of fields) {
    const id = which === 'predecessor' ? f.predecessor_crop_id : f.predecessor2_crop_id;
    const name = which === 'predecessor' ? f.predecessor_name : f.predecessor2_name;
    if (id && name) seen.set(id, name);
    else if (!id) hasUnknown = true;
  }
  const out = [...seen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  if (hasUnknown) out.push({ id: UNKNOWN_HISTORY, name: 'нет данных' });
  return out;
}

/** Варианты выпадашки плановой культуры — то, что уже стоит в этом варианте. */
export function planOptions(fields: readonly VariantField[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const f of fields) {
    if (f.plan_crop_id && f.plan_crop_name) seen.set(f.plan_crop_id, f.plan_crop_name);
  }
  return [...seen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/** Поиск без регистра и без разницы «ё/е» — агроном пишет как привык. */
export function matchesFieldQuery(name: string, query: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е').trim();
  if (!norm(query)) return true;
  return norm(name).includes(norm(query));
}

/**
 * Порядок — по названию поля, и только по нему.
 *
 * Соблазн поднять неназначенные наверх есть, но список полей агроном читает
 * как карту хозяйства: «АА1-1101 рядом с АА1-1102». Сортировка по состоянию
 * перетасовывала бы её при каждом назначении, и место поля в таблице зависело
 * бы от того, что ты только что нажал. Для «покажи только забытые» есть
 * отдельный фильтр — он честнее.
 */
export function filterSowingPlan(
  fields: readonly VariantField[],
  filter: SowingPlanFilter,
): VariantField[] {
  return fields
    .filter((f) => matchesFieldQuery(f.name, filter.query))
    .filter((f) => historyMatches(f.predecessor_crop_id, filter.predecessor))
    .filter((f) => historyMatches(f.predecessor2_crop_id, filter.predecessor2))
    .filter((f) => planMatches(f, filter.plan))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/** Есть ли вообще активный фильтр — от этого зависит текст пустого состояния. */
export function hasActiveFilter(filter: SowingPlanFilter): boolean {
  return !!(filter.query.trim() || filter.predecessor || filter.predecessor2 || filter.plan);
}

export interface SowingPlanTotals {
  fieldsTotal: number;
  arableHa: number;
  assignedFields: number;
  cropsAreaHa: number;
  fallowFields: number;
  unassignedFields: number;
  unassignedHa: number;
}

/**
 * Итоги шапки. Знаменатель — ВСЯ пашня варианта, как на экране структуры
 * (СПП): числитель и знаменатель обязаны считаться одним фильтром, иначе доли
 * разъезжаются и «полей: −3» становится возможным. Множество полей нам уже
 * отдала ручка варианта — те же поля, что в знаменателе СПП.
 */
export function sowingPlanTotals(fields: readonly VariantField[]): SowingPlanTotals {
  const totals: SowingPlanTotals = {
    fieldsTotal: fields.length,
    arableHa: 0,
    assignedFields: 0,
    cropsAreaHa: 0,
    fallowFields: 0,
    unassignedFields: 0,
    unassignedHa: 0,
  };
  for (const field of fields) {
    totals.arableHa += field.area_ha;
    const status = fieldPlanStatus(field);
    // Поле, засеянное по зонам, культуру всё-таки несёт — просто правится не
    // здесь. Считать его «неназначенным» значило бы звать агронома чинить то,
    // что не сломано.
    if (status === 'assigned' || status === 'by_subzones') {
      totals.assignedFields += 1;
      totals.cropsAreaHa += field.area_ha;
    } else if (status === 'fallow') {
      totals.fallowFields += 1;
    } else {
      totals.unassignedFields += 1;
      totals.unassignedHa += field.area_ha;
    }
  }
  return totals;
}

export function totalAreaOfIds(
  fields: readonly VariantField[],
  ids: ReadonlySet<string>,
): number {
  return fields.reduce((sum, f) => (ids.has(f.id) ? sum + f.area_ha : sum), 0);
}

// ── СПП: свод варианта по культурам ──────────────────────────────────────────

export interface SppRow {
  key: string;
  name: string;
  kind: 'crop' | 'fallow' | 'unassigned';
  areaHa: number;
  sharePct: number;
  /** Средневзвешенная по га — по полям, где урожайность задана. */
  yieldCHa: number | null;
  grossTons: number | null;
  /** Га без урожайности: из-за них валовка занижена, и это надо сказать. */
  areaWithoutYieldHa: number;
}

export interface SppSummary {
  rows: SppRow[];
  totalHa: number;
  grossTons: number;
  /** Есть ли культуры, у которых валовка неполная. */
  hasYieldGaps: boolean;
}

/**
 * Свод по культурам прямо из плановых посевов.
 *
 * Считаем здесь, а не берём готовый агрегат СПП, ровно по одной причине:
 * тот считает урожайность по НАЗНАЧЕННЫМ ТЕХКАРТАМ, а этот экран — про то,
 * что агроном ввёл в строке поля. Показать рядом с введённой в строке
 * урожайностью цифру из другого источника значило бы, что таблица и карточка
 * спорят друг с другом на одном экране.
 *
 * Средняя урожайность взвешена по га и считается ТОЛЬКО по полям, где она
 * задана: усреднять с нулями значит занижать цифру молча. Га без урожайности
 * возвращаются отдельно — валовка по ним не посчитана, и это видно.
 *
 * Валовка: га × ц/га = центнеры, делим на 10 → тонны.
 */
export function sowingPlanSpp(fields: readonly VariantField[]): SppSummary {
  const acc = new Map<
    string,
    { name: string; kind: SppRow['kind']; areaHa: number; yieldArea: number; yieldWeight: number }
  >();
  let totalHa = 0;

  for (const f of fields) {
    totalHa += f.area_ha;
    const status = fieldPlanStatus(f);
    let key: string;
    let name: string;
    let kind: SppRow['kind'];
    if (status === 'assigned' || status === 'by_subzones') {
      key = f.plan_crop_id ?? `name:${f.occupied_by ?? '—'}`;
      name = f.plan_crop_name ?? f.occupied_by ?? 'культура';
      kind = 'crop';
    } else if (status === 'fallow') {
      key = 'fallow';
      name = 'Поля отдыхают';
      kind = 'fallow';
    } else {
      key = 'unassigned';
      name = 'Культура не назначена';
      kind = 'unassigned';
    }
    const row = acc.get(key) ?? { name, kind, areaHa: 0, yieldArea: 0, yieldWeight: 0 };
    row.areaHa += f.area_ha;
    if (kind === 'crop' && f.planned_yield_c_ha !== null && f.planned_yield_c_ha > 0) {
      row.yieldArea += f.area_ha;
      row.yieldWeight += f.area_ha * f.planned_yield_c_ha;
    }
    acc.set(key, row);
  }

  const rows: SppRow[] = [...acc.entries()].map(([key, r]) => {
    const yieldCHa = r.yieldArea > 0 ? r.yieldWeight / r.yieldArea : null;
    return {
      key,
      name: r.name,
      kind: r.kind,
      areaHa: r.areaHa,
      sharePct: totalHa > 0 ? (r.areaHa / totalHa) * 100 : 0,
      yieldCHa,
      // Валовка честно считается только по той площади, у которой есть
      // урожайность: домножать на среднюю всю площадь — выдумывать тонны.
      grossTons: r.yieldArea > 0 ? r.yieldWeight / 10 : null,
      areaWithoutYieldHa: r.kind === 'crop' ? r.areaHa - r.yieldArea : 0,
    };
  });

  // Культуры по убыванию площади, служебные строки — всегда в конце.
  const order: Record<SppRow['kind'], number> = { crop: 0, fallow: 1, unassigned: 2 };
  rows.sort((a, b) => order[a.kind] - order[b.kind] || b.areaHa - a.areaHa);

  return {
    rows,
    totalHa,
    grossTons: rows.reduce((sum, r) => sum + (r.grossTons ?? 0), 0),
    hasYieldGaps: rows.some((r) => r.kind === 'crop' && r.areaWithoutYieldHa > 0.5),
  };
}

/** Что показать в колонке «Культура 20XX». Пустая ячейка читается как поломка. */
export function cropCellText(field: VariantField): string {
  const status = fieldPlanStatus(field);
  if (status === 'assigned') return field.plan_crop_name ?? field.occupied_by ?? 'культура';
  if (status === 'by_subzones') return field.occupied_by ?? 'засеяны зоны поля';
  if (status === 'fallow') return field.occupied_by ?? 'поле отдыхает';
  return 'культура не назначена';
}

/** Предшественник строки — факт прошлого сезона на ЭТОМ поле. */
export function predecessorText(field: VariantField): string {
  return field.predecessor_name
    ? field.predecessor_name.toLowerCase()
    : 'предшественник неизвестен';
}
